import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Star, Phone } from 'lucide-react';
import { initScrollAnimations, scrollToSection } from '../utils/animations';
import { API_ENDPOINTS } from '../config/api';
import { REVIEWS, OVERALL_RATING_TEXT } from '../constants/reviews';
import { useTours } from '../contexts/ToursContext';
import T from '../i18n/T';

// Lowest live tour price (Mini Tour) — used only until ToursContext has loaded,
// or if every tour is hidden. Keep in sync with the D1 tours table.
const FALLBACK_LOWEST_PRICE = 104.99;

// Verified Google rating (src/constants/reviews.js) → number of filled stars.
const FILLED_STARS = Math.max(0, Math.min(5, Math.round(REVIEWS.google.rating)));

const Hero = () => {
  const [heroImage, setHeroImage] = useState('/images/hero-bg-640.webp?v=2');
  const { tours } = useTours();

  // "Private rides from $X" — the cheapest tour a guest can actually see and
  // book (visible, not event-only). Never a hand-typed number.
  const lowestPrice = useMemo(() => {
    const prices = (Array.isArray(tours) ? tours : [])
      .filter((tour) => tour && tour.is_visible && !tour.is_event_only)
      .map((tour) => Number(tour.price))
      .filter((price) => Number.isFinite(price) && price > 0);
    return prices.length ? Math.min(...prices) : FALLBACK_LOWEST_PRICE;
  }, [tours]);
  const lowestPriceText = `$${lowestPrice.toFixed(2)}`;

  useEffect(() => {
    initScrollAnimations();
  }, []);

  useEffect(() => {
    // Delay API fetch so LCP paints with the preloaded static image first
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(`${API_ENDPOINTS.media}?section=hero_bg`);
        if (response.ok) {
          const heroSection = await response.json();
          if (heroSection?.current_image && !heroSection.current_image.includes('hero-bg')) {
            // Only swap if admin set a completely different image — preserve preloaded LCP asset
            const img = new Image();
            img.onload = () => setHeroImage(heroSection.current_image);
            img.src = heroSection.current_image;
          }
        }
      } catch {
        // Silently fall back to preloaded static image
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    // The fixed header (plus the promo bar when live) covers the top of the
    // viewport. Its measured height is --header-height (Header.js, one runtime
    // measurement; styles/fix-header-overlap.css carries the no-JS estimate), so
    // the hero reserves exactly that much and centres its content in what is
    // left of the viewport — the H1 can no longer start behind the menu.
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ paddingTop: 'var(--header-height, 80px)' }}
    >
      {/* Background. The overlay is a warm bottom-weighted gradient rather than a
          flat scrim: the photograph stays golden where the horse is, and the
          darkest ground sits exactly under the CTA block that needs contrast. */}
      <div className="absolute inset-0 z-0">
        <img
          src={heroImage}
          srcSet="/images/hero-bg-640.webp?v=2 640w, /images/hero-bg-1024.webp?v=2 1024w, /images/hero-bg.webp 1920w"
          sizes="100vw"
          alt="NYC Horse Carriage Ride in Central Park"
          className="w-full h-full object-cover ken-burns"
          loading="eager"
          fetchpriority="high"
          width="1920"
          height="1080"
          style={{ aspectRatio: '16/9' }}
        />
        <div className="absolute inset-0 z-10 bg-gradient-to-b from-neutral-950/55 via-neutral-950/40 to-neutral-950/80" />
        <div className="absolute inset-0 z-10 bg-brand-950/15" />
      </div>

      {/* Content — the header offset comes from the section's padding-top, so
          only a small breathing gap is kept here. pb-28 clears the mobile CTA bar.
          Three groups, not five stacked lines: statement, action, credentials. */}
      <div className="relative z-20 container-custom text-center text-white pt-8 md:pt-10 pb-28 md:pb-8">
        <div className="max-w-4xl mx-auto">

          {/* 1 — The statement. Headline and price read as one block. */}
          <h1
            className="font-display font-semibold text-[2.5rem] sm:text-5xl md:text-7xl lg:text-8xl mb-4 md:mb-5 leading-[1.05] tracking-[-0.02em]"
            style={{ textShadow: '0 2px 40px rgba(0,0,0,0.35)', color: '#ffffff', textWrap: 'balance' }}
            data-speakable="true"
          >
            <T>Your Central Park Carriage Ride Awaits</T>
          </h1>

          <p
            className="hero-description text-base sm:text-xl md:text-2xl mb-9 md:mb-11 text-white/85 max-w-2xl mx-auto leading-relaxed font-body"
            data-speakable="true"
          >
            <T>Private rides from</T> <span className="text-brand-200">{lowestPriceText}</span> <T>per carriage</T>
          </p>

          {/* 2 — The action. One primary, one quiet; the phone number is an
              alternative, not a competing shout. */}
          <div
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch animate-slideUp max-w-2xl mx-auto"
            style={{ animationDelay: '0.4s', animationFillMode: 'both' }}
          >
            <Link
              to="/book"
              className="btn-shine flex-1 group relative bg-brand-700 hover:bg-brand-800 text-white px-6 sm:px-8 py-4 sm:py-5 rounded-lg font-body font-bold text-base sm:text-lg tracking-wide transition-all duration-300 shadow-xl shadow-brand-900/30 hover:shadow-2xl hover:-translate-y-0.5 flex items-center justify-center gap-2 min-h-[60px] sm:min-h-[72px]"
              aria-label="Check availability and book your Central Park carriage ride"
            >
              <span className="relative z-10 flex items-center justify-center gap-2">
                <T>Check Availability</T>
              </span>
            </Link>

            <a
              href="tel:+16466569717"
              className="flex-1 group relative bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/35 hover:border-white/55 text-white px-6 sm:px-8 py-4 sm:py-5 rounded-lg font-body font-semibold text-base sm:text-lg tracking-wide transition-all duration-300 hover:-translate-y-0.5 flex items-center justify-center gap-2 min-h-[60px] sm:min-h-[72px]"
              onClick={() => {
                if (typeof window !== 'undefined' && window.gtag) {
                  window.gtag('event', 'phone_click', { event_category: 'engagement', event_label: 'hero_cta' });
                }
              }}
            >
              <Phone className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true" />
              <span dir="ltr">(646) 656-9717</span>
            </a>
          </div>

          <p className="mt-4 text-white/65 font-body text-xs md:text-sm tracking-wide">
            <T>Instant confirmation</T> · <T>No hidden fees</T>
          </p>

          {/* 3 — The credentials. One quiet line under a hairline rule, carrying
              the verified Google figure and the heritage the old "Central Park"
              pill was gesturing at without saying anything (removed 2026-09-08:
              the word already appeared in the headline, the nav and the address
              bar within 200px of it). */}
          <div
            className="mt-9 md:mt-11 animate-fadeIn"
            style={{ animationDelay: '0.6s', animationFillMode: 'both' }}
          >
            <div className="mx-auto mb-5 h-px w-16 bg-brand-300/40" aria-hidden="true" />
            <p className="flex flex-wrap items-center justify-center gap-x-2.5 gap-y-1 text-xs md:text-sm text-white/70 font-body">
              <span className="flex items-center gap-1">
                {Array.from({ length: FILLED_STARS }, (_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 text-brand-300 fill-brand-300" aria-hidden="true" />
                ))}
                <span className="ml-1 font-semibold text-white/90">{OVERALL_RATING_TEXT}</span>
              </span>
              <span className="text-white/30" aria-hidden="true">·</span>
              <Link
                to="/reviews"
                className="underline underline-offset-4 decoration-white/30 hover:decoration-white/80 hover:text-white transition-colors"
              >
                {REVIEWS.google.count} <T>Google reviews</T>
              </Link>
              <span className="text-white/30" aria-hidden="true">·</span>
              <span><T>Family-run in Central Park since 1999</T></span>
            </p>
          </div>
        </div>

      </div>

      {/* Scroll indicator — a child of the SECTION, not of the content
          container: anchored to the container it would land on top of the
          credential line (measured collision, 2026-09-08). Hidden on mobile
          to avoid the sticky CTA bar. */}
      <div
        className="hidden md:block absolute bottom-8 left-1/2 -translate-x-1/2 z-20 animate-fadeIn"
        style={{ animationDelay: '1.4s', animationFillMode: 'both' }}
      >
        <button
          onClick={() => scrollToSection('#about')}
          className="text-white/30 hover:text-white/60 transition-colors duration-300 animate-bounce"
          aria-label="Scroll to about section"
        >
          <ChevronDown className="w-7 h-7" aria-hidden="true" />
        </button>
      </div>
    </section>
  );
};

export default Hero;
